/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useMemo, useRef, useState } from "react";
import { buildDetailPath } from "@shared/navigation/tabs/detailRoutes";
import { CapacityAllocationChart } from "kontrolEdge/components/Charts/CapacityAllocationChart";
import { DataCenterCapacityChart } from "kontrolEdge/components/Charts/DatacenterCapacityChart";
import { PortCapacityChart } from "kontrolEdge/components/Charts/PortCapacityChart";
import { ServiceKind, ServiceStatusCard } from "./OverviewComponents/ServiceStatusCard";
import { ChartCard } from "./OverviewComponents/ChartCards";

import { useAppSelector } from "@redux/hooks";
import { RefreshingIndicator } from "@shared/components/ListLoading";
import { PortUsageList } from "./OverviewComponents/PortUsageList";
import {
  FailoverItem,
  FailoverTableRow,
  Port,
} from "./OverviewComponents/Types";
import {
  GenericTable,
  GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import { LoadingWrapper } from "kontrolEdge/components/Shared/LoadingWrapper";
import EmptyItems from "@shared/components/EmptyItems";
import MonitoringUnavailable from "./OverviewComponents/MonitoringUnavailable";
import {
  useMaintenanceWindow,
  useUpcomingWindow,
} from "@shared/hooks/useMaintenanceStatus";
import GenericButton from "@ui-components/Button/GenericButton";
import { useManagerOverviewData } from "./OverviewComponents/hooks/useManagerOverviewData";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import {
  normalizeToUnit,
  useManagerOverviewComputedData,
} from "./OverviewComponents/hooks/useManagerOverviewComputedData";
import { formatDuration } from "kontrolEdge/helpers/formatDuration";
import { buildPlatformMaintenanceRow } from "kontrolEdge/helpers/platformMaintenanceRow";
import { DurationBadge } from "kontrolEdge/helpers/DurationBadge";
import {
  buildPredicate,
  FilterField,
  labelId,
  parseToken,
  ParsedFilter,
} from "kontrolEdge/helpers/advancedFilters";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import { makeOutageFields } from "./OverviewComponents/outageFieldSpec";
import { useNavigate } from "react-router-dom";
import { chunk, useHScroll } from "./OverviewComponents/hooks/useHScroll";
import { SkeletonTableRows } from "@ui-components/Skeleton/GenericSkeleton";
import GenericModal from "@ui-components/Modal/GenericModal";

function ManagerOverview() {
  const api = useAxiosAuth0();
  const { tenantId, tenantName, partnerId } = useAppSelector(
    (state) => state.selection
  );
  const isViewingAllCustomers = tenantId === partnerId;

  const selection = useAppSelector((s) => s.selection);
  const tenantType = useAppSelector((state) => state.userProfile.TenantType);
  let currentView: "GLOBAL_ADMIN" | "PARTNER" | "CUSTOMER" = tenantType as any;
  if (tenantType === "GLOBAL_ADMIN") {
    if (
      selection.partnerId &&
      selection.tenantId &&
      selection.partnerId !== selection.tenantId
    ) {
      currentView = "CUSTOMER";
    } else if (selection.partnerId) {
      currentView = "PARTNER";
    }
  }

  const TITLE_TO_KIND: Record<string, ServiceKind> = {
    "Enterprise Internet": "internet",
    "Ports": "port",
    "Cloud Connectivity": "cloud",
    "Data Center Interconnect": "datacenter",
    "FWaaS": "fwaas",
    "SD-WAN": "sdwan",
  };

  // General flag to show "Empty Items" if no service status is available.
  // Cached per tenant inside the hook: coming back paints the last overview at once and
  // refreshes it underneath; each section keeps its own skeleton for a first visit.
  const { data, loading, hasData, isServiceFetched, hasServiceError, isRefreshing } =
    useManagerOverviewData(api, tenantId);
  const {
    filteredFailoverRecords,
    filteredMaintenanceRecords,
    filteredUsagePorts,
    serviceCards,
    aggregatedAllocated,
    capacityAllocationData,
    totalCapacity,
    totalAllocated,
    portCapacityPieData,
    legendDataPortCapacity,
    chartData,
    showPortCapacity,
    showDataCenterCapacity,
    showCapacityAllocationChart,
    datacenterCapacityData,
    datacenterCapacityLegend,
    chartUnit,
    portChartUnit,
  } = useManagerOverviewComputedData({
    data,
    tenantId,
    tenantName,
    isViewingAllCustomers,
    currentView,
  });

  const navigate = useNavigate();
  const isFetchingService = loading.service;

  const serviceStatusData = data.serviceStatus;
  const usageData = data.usage;
  const failover = data.failover;
  const maintenance = data.maintenance;

  const isFetchingUsage = loading.usage;
  const isFetchingFailover = loading.failover;
  const isFetchingMaintenance = loading.maintenance;

  const [isOutageModalOpen, setIsOutageModalOpen] = useState(false);
  const [selectedOutage, setSelectedOutage] = useState<FailoverTableRow | null>(null);
  const [outageActiveFilters, setOutageActiveFilters] = useState<ParsedFilter[]>(
    []
  );

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const handleScrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 320,
        behavior: "smooth",
      });
    }
  };
  const rows = useMemo(() => chunk(serviceCards, 3), [serviceCards]);
  const statusScroll = useHScroll(320, [serviceCards.length]);

  const handlePortCardClick = useCallback(
    (port: Port) => {
      if (!port?.id) return;
      navigate(buildDetailPath("port", { portId: port.id }), {
        state: {
          selectedPortRow: port,
          portIdString: port.subscriptionId,
          from: "usage-list",
        },
      });
    },
    [navigate]
  );
  //------Loading the failover data inside the table in Outages
  const tableData: FailoverTableRow[] = useMemo(
    () =>
      filteredFailoverRecords
        .map(({ date, description, duration, customer, site, summary, detail, incidentId }: FailoverItem) => ({
          date,
          description,
          customer: customer?.name ?? "–",
          duration: formatDuration(duration),
          site: site ?? "–",
          summary: summary ?? "",
          detail: detail ?? "",
          incidentId: incidentId ?? "",
        })) ?? [],
    [filteredFailoverRecords]
  );

  const outageFields = useMemo(
    () => makeOutageFields(isViewingAllCustomers),
    [isViewingAllCustomers]
  );

  const outageFieldById = useMemo(() => {
    const m = new Map<string, FilterField<FailoverTableRow>>();
    for (const f of outageFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      m.set(id, f);
    }
    return m;
  }, [outageFields]);

  const outageKeyMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of outageFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      const lbl = f.label.toLowerCase();
      m.set(id, id);
      m.set(lbl, id);
    }
    return m;
  }, [outageFields]);

  const outageNormalizedFilters = useMemo(() => {
    return outageActiveFilters.map((f) => {
      const token = f.op
        ? { ...f }
        : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

      if (token.key) {
        const k = token.key.trim().toLowerCase();
        token.key = outageKeyMap.get(k) ?? labelId(token.key);
      }

      const field = token.key ? outageFieldById.get(token.key) : undefined;
      if (field?.type === "string") {
        const quoted = token.value.match(/^"(.*)"$/);
        if (quoted) {
          token.value = quoted[1];
          token.op = "=";
        } else {
          token.op = ":";
        }
      }
      return token;
    });
  }, [outageActiveFilters, outageKeyMap, outageFieldById]);

  const outagePredicate = useMemo(
    () =>
      buildPredicate<FailoverTableRow>(outageNormalizedFilters, outageFields),
    [outageNormalizedFilters, outageFields]
  );

  const filteredOutageRows = useMemo(
    () => tableData.filter(outagePredicate),
    [tableData, outagePredicate]
  );

  const outageSuggestedKeys = useMemo(() => {
    const keys = ["ID", "Date", "Site"];
    if (isViewingAllCustomers) keys.push("Customer");
    keys.push("Duration", "Description", "Detail");
    return keys;
  }, [isViewingAllCustomers]);

  const outageColumns: GenericTableColumn<FailoverTableRow>[] = useMemo(
    () => {
      const cols: GenericTableColumn<FailoverTableRow>[] = [
        {
          header: "ID",
          accessor: "incidentId",
          sortAccessor: (row) => row.incidentId ?? "",
          renderCell: (row) => (
            <span className="text-xs text-semantic-text-secondary whitespace-nowrap">
              {row.incidentId || "–"}
            </span>
          ),
        },
        {
          header: "Date",
          accessor: "date",
          renderCell: (row) => (
            <span className="whitespace-nowrap font-semibold text-semantic-text-primary">
              {row.date}
            </span>
          ),
        },
        {
          header: "Site",
          accessor: "site",
          renderCell: (row) =>
            row.site && row.site !== "–" ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-semantic-text-primary">
                {row.site}
              </span>
            ) : null,
        },
      ];

      if (isViewingAllCustomers) {
        cols.push({
          header: "Customer",
          accessor: "customer",
          sortAccessor: (row) => row.customer ?? "",
          renderCell: (row) => (
            <p className="text-sm text-semantic-text-secondary">
              {row.customer}
            </p>
          ),
        });
      }

      cols.push(
        {
          header: "Duration",
          accessor: "duration",
          sortAccessor: (row) => row.duration,
          renderCell: (row) => (
            <span className="font-semibold text-semantic-text-primary">
              <DurationBadge value={row.duration} />
            </span>
          ),
        },
        {
          header: "Details",
          accessor: "detail",
          renderCell: (row) =>
            row.detail ? (
              <button
                type="button"
                className="text-xs text-semantic-text-brand hover:text-semantic-text-primary underline"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedOutage(row);
                  setIsOutageModalOpen(true);
                }}
              >
                Details
              </button>
            ) : null,
        }
      );

      return cols;
    },
    [isViewingAllCustomers]
  );

  //------Loading the maintenance data inside the table in Outages
  /**
   * The platform window as a row in this table — the one in progress, or the
   * one announced for later.
   *
   * The scheduled case is the one that matters: in `blocked` mode the
   * telemetry views are replaced while the window runs, so a notice that
   * only appears once maintenance starts is a notice nobody reads. See
   * helpers/platformMaintenanceRow for the wording and the attribution.
   */
  const platformMaintenanceWindow = useMaintenanceWindow("Kontrol Edge");
  const upcomingMaintenanceWindow = useUpcomingWindow("Kontrol Edge");

  const platformMaintenanceRow: FailoverTableRow | null = useMemo(
    () =>
      buildPlatformMaintenanceRow(
        platformMaintenanceWindow,
        upcomingMaintenanceWindow
      ),
    [platformMaintenanceWindow, upcomingMaintenanceWindow]
  );

  const maintenanceData: FailoverTableRow[] = useMemo(() => {
    const records =
      filteredMaintenanceRecords
        .slice(0, 5)
        .map(({ date, description, duration, customer }: FailoverItem) => ({
          date,
          description,
          customer: customer?.name ?? "–",
          duration: formatDuration(duration),
        })) ?? [];

    // First: it is the one that is happening now, and the table is ordered
    // newest first.
    return platformMaintenanceRow ? [platformMaintenanceRow, ...records] : records;
  }, [filteredMaintenanceRecords, platformMaintenanceRow]);

  //------Loading columns for the maintenance table
  const maintenanceColumns: GenericTableColumn<FailoverTableRow>[] = useMemo(() => {
    const cols: GenericTableColumn<FailoverTableRow>[] = [
      {
        header: "Date",
        accessor: "date",
        renderCell: (row) =>
          isFetchingMaintenance ? (
            <div className="h-4 w-24 bg-gray-300 animate-pulse rounded" />
          ) : (
            row.date
          ),
      },
      {
        header: "Description",
        accessor: "description",
        renderCell: (row) =>
          isFetchingMaintenance ? (
            <div className="h-4 w-48 bg-gray-300 animate-pulse rounded" />
          ) : (
            row.description
          ),
      },
    ];

    if (isViewingAllCustomers) {
      cols.push({
        header: "Customer",
        accessor: "customer",
        renderCell: (row) =>
          isFetchingMaintenance ? (
            <div className="h-4 w-24 bg-gray-300 animate-pulse rounded" />
          ) : (
            <p className="text-sm text-semantic-text-secondary">
              {row.customer}
            </p>
          ),
      });
    }

    cols.push({
      header: "Duration",
      accessor: "duration",
      renderCell: (row) =>
        isFetchingMaintenance ? (
          <div className="h-4 w-16 bg-gray-300 animate-pulse rounded" />
        ) : (
          row.duration
        ),
    });

    return cols;
  }, [isViewingAllCustomers, isFetchingMaintenance]);

  //Skeleton
  const tableSkeleton = (
    <SkeletonTableRows rows={3} cols={1} rowHeight={16} />
  );

  return (
    <div className="relative">
      <RefreshingIndicator active={isRefreshing} />
      {/* "Nothing came back" has two causes with opposite meanings: the tenant
          really has no services, or the request failed. Only the first one is
          an empty state. */}
      {isServiceFetched && !hasData ? (
        hasServiceError ? <MonitoringUnavailable /> : <EmptyItems />
        ) : (


        <>
          {/* Service Status */}
          <div className="mt-4 rounded-md border border-gray-300 bg-semantic-bg-primary mx-3">
            <h2 className="text-md px-6 py-2 mb-4 font-bold text-semantic-text-primary text-left border-b">
              Service Status
            </h2>
              <div className="py-4 px-4">
                <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6 items-stretch">
                {rows.flat().map(({ title, path, data, showWarning }) => {
                  const service: ServiceKind = TITLE_TO_KIND[title] ?? "internet";
                  return (
                    // <div key={path} className="w-full h-full flex flex-col">
                    <div key={path} className="w-full flex flex-col">
                      <ServiceStatusCard
                        title={title}
                        data={data}
                        isLoading={isFetchingService}
                        path={path}
                        showWarning={showWarning}
                        service={service}
                      />
                    </div>
                  );
                  })}
                </div>
              </div>
          </div>

          {/* Charts Section */}
          <div className="mt-4 rounded-md border border-gray-300 bg-semantic-bg-primary mx-3">
            <h2 className="text-md px-6 py-2 mb-4 font-bold text-semantic-text-primary text-left border-b">
              Performance Metrics
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5 px-4 items-stretch">
              {showPortCapacity && (
                <ChartCard
                  title={
                    isViewingAllCustomers
                      ? "Total allocated by customer"
                      : "Total port capacity"
                  }
                  subtitle={isViewingAllCustomers ? "" : "Allocated / Total"}
                  chart={
                    <PortCapacityChart
                      data={chartData}
                      isBreakdown={isViewingAllCustomers}
                      totalCapacityGbps={normalizeToUnit(
                        totalCapacity,
                        portChartUnit
                      )}
                      isViewingAll={isViewingAllCustomers}
                      centerValueMbps={totalAllocated}
                    />
                  }
                  legendData={legendDataPortCapacity}
                  unit={portChartUnit}
                  hideValue={true}
                  isLoading={isFetchingService}
                />
              )}

              {showDataCenterCapacity && (
                <ChartCard
                  title="Data center port capacity"
                  subtitle="Allocated vs Available"
                  chart={
                    <DataCenterCapacityChart
                      data={datacenterCapacityData}
                      unit={chartUnit}
                    />
                  }
                  legendData={datacenterCapacityData}
                  extraLegendData={datacenterCapacityLegend}
                  unit={chartUnit}
                  isLoading={isFetchingService}
                />
              )}

              {showCapacityAllocationChart && (
                <ChartCard
                  title="Capacity allocation distribution"
                  subtitle=""
                  chart={
                    <CapacityAllocationChart data={capacityAllocationData} />
                  }
                  legendData={capacityAllocationData}
                  isLoading={isFetchingService}
                />
              )}
            </div>
            {(isFetchingUsage ||
              (usageData?.data && usageData.data.length > 0)) && (
              <div className="relative py-4">
                <div className="px-4">
                  {/* Scrollable container WITH REF */}
                  <div
                    ref={scrollContainerRef}
                    className="overflow-x-auto"
                    style={{ scrollbarGutter: "stable both-edges" }}
                  >
                    <div className="flex gap-5 pr-4">
                      {usageData?.data && (
                        <LoadingWrapper
                          isLoading={isFetchingUsage}
                          skeleton={tableSkeleton}
                        >
                          <PortUsageList
                            ports={filteredUsagePorts}
                            onCardClick={handlePortCardClick}
                          />
                        </LoadingWrapper>
                      )}
                    </div>
                  </div>
                  {/* Floating scroll button */}
                  {filteredUsagePorts.length > 2 && (
                    <div className="absolute right-0 top-1/2 transform -translate-y-1/2 pr-2 z-20">
                      <GenericButton
                        variant="outlined"
                        size="lg"
                        iconRightName="arrowRight"
                        isIconOnly
                        onClick={handleScrollRight}
                        className="!bg-semantic-bg-primary/80 backdrop-blur-md"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Maintenance and Outages */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5 px-4 items-stretch">
            <div className="mt-4 rounded-md border border-gray-300 bg-semantic-bg-primary md:mx-0 lg:mx-0">
              <h2 className="text-md px-6 py-2 mb-4 font-bold text-semantic-text-primary text-left border-b">
                Outages
              </h2>
              <div className="mb-5 px-4">
                <LoadingWrapper
                  isLoading={isFetchingFailover}
                  skeleton={tableSkeleton}
                >
                  {tableData.length === 0 ? (
                    <p className="text-sm text-semantic-text-secondary text-center py-6">
                      No outages recorded
                    </p>
                  ) : (
                    <div className="flex flex-col min-h-0">
                      <div className="mb-4 bg-[var(--semantic-bg-primary)] flex-shrink-0">
                        <div className="w-full max-w-full sm:max-w-[500px] my-3">
                          <GenericSearchbar
                            placeholder="Find by attribute or value"
                            suggestedKeys={outageSuggestedKeys}
                            onFiltersChange={setOutageActiveFilters}
                          />
                        </div>
                      </div>
                      <GenericTable
                        columns={outageColumns}
                        data={filteredOutageRows}
                        initialRowsPerPage={10}
                        rowsPerPageOptions={[10, 20, 30]}
                        width="100%"
                        tableClassName="bg-semantic-bg-primary"
                        maxTableBodyHeight="400px"
                        verticalAlign="top"
                        horizontalScroll="xl-fit"
                        rowKey={(row, index) =>
                          row.incidentId
                            ? `${row.incidentId}-${row.date}-${index}`
                            : `outage-${index}`
                        }
                        renderRowBelow={(row) =>
                          row.description?.trim() ? row.description : null
                        }
                      />
                    </div>
                  )}
                </LoadingWrapper>
                <div className="flex justify-start py-5 gap-5">
                  {/* <GenericTag label={`Last Check: ${formattedLastCheck}`} /> */}
                </div>
                {/* <div className='flex justify-end mb-10 mr-5'>
					<GenericButton label="See all" size="sm" onClick={() => navigate('/kontrol/outages', {state: {selectedView: "outages"}})} disabled={true}/>
				</div> */}
              </div>
            </div>
            <div className="mt-4 rounded-md border border-gray-300 bg-semantic-bg-primary md:mx-0 lg:mx-0">
              <h2 className="text-md px-6 py-2 mb-4 font-bold text-semantic-text-primary text-left border-b">
                Maintenance
              </h2>
              <div className="mb-5 px-4">
                <LoadingWrapper
                  isLoading={isFetchingMaintenance}
                  skeleton={tableSkeleton}
                >
                  <GenericTable
                    columns={maintenanceColumns}
                    data={
                      isFetchingMaintenance
                        ? Array(5).fill({})
                        : maintenanceData
                    }
                    tableClassName="w-full"
                    isFooterVisible={false}
                    horizontalScroll="xl-fit"
                  />
                </LoadingWrapper>
                {/* <div className='flex justify-end mb-10 mr-5'>
					<GenericButton label="See all" size="sm" onClick={() => navigate('/kontrol/outages', {state: {selectedView: "maintenance"}})}  disabled={false}/>
				</div> */}
              </div>
            </div>
          </div>
        </>
      )}

      <GenericModal
        isOpen={isOutageModalOpen}
        onClose={() => {
          setIsOutageModalOpen(false);
          setSelectedOutage(null);
        }}
        title="Outage Details"
        size="lg"
      >
        {selectedOutage && (
          <div className="space-y-4 p-4">
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-semantic-text-secondary">
              {selectedOutage.incidentId && (
                <span><strong>ID:</strong> {selectedOutage.incidentId}</span>
              )}
              <span><strong>Date:</strong> {selectedOutage.date}</span>
              {selectedOutage.site && selectedOutage.site !== "–" && (
                <span><strong>Site:</strong> {selectedOutage.site}</span>
              )}
              <span><strong>Duration:</strong> {selectedOutage.duration}</span>
            </div>

            {selectedOutage.detail && (
              <div>
                <p className="text-sm text-semantic-text-secondary whitespace-pre-wrap text-justify">
                  {selectedOutage.detail}
                </p>
              </div>
            )}

            <div className="pt-3 border-t border-gray-200">
              <p className="text-xs text-semantic-text-secondary">
                For more information, open a{" "}
                <a
                  href="/support/request"
                  className="underline hover:text-semantic-text-primary"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsOutageModalOpen(false);
                    setSelectedOutage(null);
                    navigate("/support/request");
                  }}
                >
                  help request
                </a>
                .
              </p>
            </div>
          </div>
        )}
      </GenericModal>
    </div>
  );
}

export default ManagerOverview;
